import { useState, useEffect, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import idx from "idx";
import { getPlatformConnections, syncPlatformContacts, previewPullContacts, getSyncJobStatus } from "../../../api/accounting.api";
import { getCustomerList } from '../../../actions/customer-management.action';
import { getVendorList } from '../../../actions/vendor-management.action';
import { errorSnackBar, successSnackBar } from "../../../actions/common.action";
import { loaders, SYNC_POLL_INTERVALS } from "../../../constants";

// Tenant-scoped caches for connections and contact lists to minimize database and platform queries
let cachedAccountId = null;
let cachedConnections = null;
let cachedConnectionsPromise = null;
let cachedCustomers = null;
let cachedCustomersPromise = null;
let cachedSuppliers = null;
let cachedSuppliersPromise = null;
let cachedContractors = null;
let cachedContractorsPromise = null;

function checkAndFlushTenantCache(currentAccountId) {
    if (cachedAccountId && currentAccountId && String(cachedAccountId) !== String(currentAccountId)) {
        cachedConnections = null;
        cachedConnectionsPromise = null;
        cachedCustomers = null;
        cachedCustomersPromise = null;
        cachedSuppliers = null;
        cachedSuppliersPromise = null;
        cachedContractors = null;
        cachedContractorsPromise = null;
    }
    if (currentAccountId) {
        cachedAccountId = currentAccountId;
    }
}

export function useSyncContacts({ show, entityType, connectionId, localContacts: propLocalContacts, onSyncSuccess, onHide }) {
    const dispatch = useDispatch();
    const user = useSelector(state => state.user.userData);
    const accountId = idx(user, _ => _.selected_account.id);
    const userId = idx(user, _ => _.id);
    const isMountedRef = useRef(true);

    const [connections, setConnections] = useState([]);
    const [selectedConnection, setSelectedConnection] = useState(null);
    const [syncDirection, setSyncDirection] = useState('push');
    const [syncing, setSyncing] = useState(false);
    const [selectedIds, setSelectedIds] = useState([]);
    const [conflicts, setConflicts] = useState([]);
    const [showConflictModal, setShowConflictModal] = useState(false);
    const [fetchingPreview, setFetchingPreview] = useState(false);
    const [resolutions, setResolutions] = useState({});
    const [showAllSyncContacts, setShowAllSyncContacts] = useState(false);
    const [syncLogs, setSyncLogs] = useState({});
    const [syncProgress, setSyncProgress] = useState({ total: 0, current: 0 });

    // For 'all' mode (settings page)
    const [loadingMetadata, setLoadingMetadata] = useState(false);
    const [modalCustomers, setModalCustomers] = useState([]);
    const [modalSuppliers, setModalSuppliers] = useState([]);
    const [modalContractors, setModalContractors] = useState([]);
    const [activeModalTab, setActiveModalTab] = useState('customer');
    const [syncHappened, setSyncHappened] = useState(false);
    const syncHappenedRef = useRef(false);

    const permissions = user?.permissions || {};
    const hasCustomerWrite = Boolean(user?.global_user || permissions?.customer === 2);
    const hasSupplierWrite = Boolean(user?.global_user || permissions?.supplier === 2);
    const hasContractorWrite = Boolean(user?.global_user || permissions?.contractor === 2);

    useEffect(() => {
        const allowedTab = hasCustomerWrite ? 'customer' : (hasSupplierWrite ? 'supplier' : (hasContractorWrite ? 'contractor' : 'customer'));
        if (activeModalTab === 'customer' && !hasCustomerWrite) {
            setActiveModalTab(allowedTab);
        } else if (activeModalTab === 'supplier' && !hasSupplierWrite) {
            setActiveModalTab(allowedTab);
        } else if (activeModalTab === 'contractor' && !hasContractorWrite) {
            setActiveModalTab(allowedTab);
        }
    }, [activeModalTab, hasCustomerWrite, hasSupplierWrite, hasContractorWrite]);

    // #12 — isMountedRef for unmount safety
    useEffect(() => {
        isMountedRef.current = true;
        return () => { isMountedRef.current = false; };
    }, []);

    const entitlements = useSelector((state) => state.user.entitlements) || {};

    // Load connections
    const fetchConnections = useCallback(async () => {
        if (!accountId) return;
        checkAndFlushTenantCache(accountId);
        try {
            let activeConns = [];
            if (cachedConnections) {
                activeConns = cachedConnections;
            } else {
                if (cachedConnectionsPromise) {
                    try {
                        const conns = await cachedConnectionsPromise;
                        activeConns = conns.filter(c => c.is_active);
                    } catch (err) {
                        cachedConnectionsPromise = null;
                    }
                }
                if (!cachedConnectionsPromise) {
                    cachedConnectionsPromise = getPlatformConnections(false);
                    const conns = await cachedConnectionsPromise;
                    activeConns = conns.filter(c => c.is_active);
                    cachedConnections = activeConns;
                }
            }

            if (!isMountedRef.current) return;

            const filteredConns = activeConns.filter(c => {
                if (c.platform === 'xero' && entitlements.xeroIntegration === false) return false;
                if (c.platform === 'quickbooks' && entitlements.qboIntegration === false) return false;
                if (c.platform === 'myob' && entitlements.myobIntegration === false) return false;
                return true;
            });

            setConnections(filteredConns);

            const isConnPushAllowed = (c) => {
                if (!c) return false;
                if (c.platform === 'xero') return entitlements.xeroPush !== false;
                if (c.platform === 'quickbooks') return entitlements.qboPush !== false;
                if (c.platform === 'myob') return entitlements.myobPush !== false;
                return entitlements.accountingPush !== false;
            };

            let initialConn = null;
            if (connectionId) {
                initialConn = filteredConns.find(c => String(c.id) === String(connectionId));
            }
            if (!initialConn) {
                initialConn = filteredConns.find(c => c.is_default && isConnPushAllowed(c))
                    || filteredConns.find(c => isConnPushAllowed(c))
                    || filteredConns.find(c => c.is_default)
                    || filteredConns[0];
            }
            if (!selectedConnection || (connectionId && String(selectedConnection.id) !== String(connectionId))) {
                setSelectedConnection(initialConn || null);
            }
        } catch (err) {
            console.error("Failed to load platform connections:", err);
            cachedConnectionsPromise = null;
        }
    }, [accountId, connectionId, selectedConnection, entitlements]);

    const isPushAllowed = selectedConnection ? (() => {
        if (selectedConnection.platform === 'xero') return entitlements.xeroPush !== false;
        if (selectedConnection.platform === 'quickbooks') return entitlements.qboPush !== false;
        if (selectedConnection.platform === 'myob') return entitlements.myobPush !== false;
        return entitlements.accountingPush !== false;
    })() : true;

    const isPullAllowed = selectedConnection ? (() => {
        if (selectedConnection.platform === 'xero') return entitlements.xeroPull !== false;
        if (selectedConnection.platform === 'quickbooks') return entitlements.qboPull !== false;
        if (selectedConnection.platform === 'myob') return entitlements.myobPull !== false;
        return entitlements.accountingPull !== false;
    })() : true;

    useEffect(() => {
        if (selectedConnection) {
            if (!isPushAllowed && syncDirection === 'push' && isPullAllowed) {
                setSyncDirection('pull');
            } else if (!isPullAllowed && syncDirection === 'pull' && isPushAllowed) {
                setSyncDirection('push');
            }
        }
    }, [selectedConnection, syncDirection, isPushAllowed, isPullAllowed]);

    // #11 — loadAllModalData wrapped in useCallback
    const loadAllModalData = useCallback(async () => {
        if (cachedCustomers && cachedSuppliers && cachedContractors) {
            setModalCustomers(cachedCustomers);
            setModalSuppliers(cachedSuppliers);
            setModalContractors(cachedContractors);
            return;
        }

        setLoadingMetadata(true);
        try {
            if (!cachedCustomersPromise) {
                cachedCustomersPromise = dispatch(getCustomerList());
            }
            if (!cachedSuppliersPromise) {
                cachedSuppliersPromise = dispatch(getVendorList('Supplier'));
            }
            if (!cachedContractorsPromise) {
                cachedContractorsPromise = dispatch(getVendorList('sub-contractor'));
            }

            const [customersRes, suppliersRes, contractorsRes] = await Promise.all([
                cachedCustomersPromise,
                cachedSuppliersPromise,
                cachedContractorsPromise
            ]);

            if (!isMountedRef.current) return;

            const formattedCustomers = (customersRes || []).map(c => {
                const primary = (c.contacts || []).find(item => item.primary) || {};
                const address = (c.siteAddress || []).find(item => item.primary) || {};
                const contactName = [primary.firstName, primary.lastName].filter(Boolean).join(' ');
                return {
                    id: c.id,
                    customerNumber: c.customerNumber || '',
                    businessName: c.businessName || '',
                    address: address.f_address || '',
                    phoneNumber: c.phoneNumber || primary.phoneNumber || '',
                    contacts: contactName,
                    email: c.email || primary.email || '',
                    type: c.type || ''
                };
            });

            const formatVendor = (v) => {
                const primary = (v.contacts || []).find(item => item.primary) || {};
                const contactName = [primary.firstName, primary.lastName].filter(Boolean).join(' ');
                return {
                    id: v.id,
                    vendorNumber: v.vendorNumber || '',
                    businessName: v.businessName || '',
                    address: v.address || '',
                    phoneNumber: v.phoneNumber || primary.phoneNumber || '',
                    contacts: contactName,
                    email: v.email || primary.email || '',
                    isActive: v.isActive
                };
            };

            const formattedSuppliers = (suppliersRes || []).map(formatVendor);
            const formattedContractors = (contractorsRes || []).map(formatVendor);

            cachedCustomers = formattedCustomers;
            cachedSuppliers = formattedSuppliers;
            cachedContractors = formattedContractors;

            setModalCustomers(formattedCustomers);
            setModalSuppliers(formattedSuppliers);
            setModalContractors(formattedContractors);
        } catch (err) {
            console.error("Failed to load contacts for sync selection:", err);
            cachedCustomersPromise = null;
            cachedSuppliersPromise = null;
            cachedContractorsPromise = null;
        } finally {
            if (isMountedRef.current) {
                setLoadingMetadata(false);
            }
        }
    }, [dispatch]);

    useEffect(() => {
        if (show) {
            setSyncHappened(false);
            fetchConnections();
            setSelectedIds([]);
            setSyncDirection('push');
            setConflicts([]);
            setResolutions({});
            setSyncLogs({});
            setShowConflictModal(false);
            if (entityType === 'all') {
                loadAllModalData();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show, entityType]);

    // #11 — getActiveContactsForTab wrapped in useCallback
    const getActiveContactsForTab = useCallback(() => {
        if (entityType === 'all') {
            if (activeModalTab === 'customer') return modalCustomers;
            if (activeModalTab === 'supplier') return modalSuppliers;
            return modalContractors;
        }
        return propLocalContacts || [];
    }, [entityType, activeModalTab, modalCustomers, modalSuppliers, modalContractors, propLocalContacts]);

    const isAllSelectedForCurrentTab = useCallback(() => {
        const currentRows = getActiveContactsForTab();
        if (currentRows.length === 0) return false;
        return currentRows.every(row => selectedIds.includes(row.id));
    }, [getActiveContactsForTab, selectedIds]);

    const toggleSelectAllForCurrentTab = useCallback(() => {
        const currentRows = getActiveContactsForTab();
        const currentIds = currentRows.map(r => r.id);
        if (isAllSelectedForCurrentTab()) {
            setSelectedIds(prev => prev.filter(id => !currentIds.includes(id)));
        } else {
            setSelectedIds(prev => {
                const otherIds = prev.filter(id => !currentIds.includes(id));
                return [...otherIds, ...currentIds];
            });
        }
    }, [getActiveContactsForTab, isAllSelectedForCurrentTab]);

    const toggleRowSelection = useCallback((id) => {
        setSelectedIds(prev =>
            prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
        );
    }, []);

    const handleFetchPullPreview = useCallback(async () => {
        if (!selectedConnection || !accountId) return;
        setFetchingPreview(true);
        try {
            const finalEntityType = entityType === 'all' ? activeModalTab : entityType;
            const res = await previewPullContacts(selectedConnection.id, finalEntityType, selectedIds);
            if (!isMountedRef.current) return;
            const conflictsList = res || [];
            setConflicts(conflictsList);
            const initialResolutions = {};
            conflictsList.forEach(contact => {
                const fieldsRes = {
                    vendor_type: contact.detectedType || ""
                };
                contact.fields.forEach(f => {
                    if (f.isConflict) {
                        if (!f.external) {
                            fieldsRes[f.name] = 'local';
                        } else {
                            fieldsRes[f.name] = 'external';
                        }
                    } else {
                        fieldsRes[f.name] = 'local';
                    }
                });
                initialResolutions[contact.externalId] = fieldsRes;
            });
            setResolutions(initialResolutions);
            setShowConflictModal(true);
        } catch (err) {
            dispatch(errorSnackBar(err.message || "Failed to preview pull conflicts"));
        } finally {
            setFetchingPreview(false);
        }
    }, [selectedConnection, accountId, entityType, activeModalTab, selectedIds, dispatch]);

    const pollSyncJob = useCallback(async (jobId, intervalMs = SYNC_POLL_INTERVALS.ACTIVE_SYNC, maxRetries = SYNC_POLL_INTERVALS.MAX_RETRIES) => {
        for (let retry = 0; retry < maxRetries; retry++) {
            if (!isMountedRef.current) throw new Error('Component unmounted');

            try {
                const statusRes = await getSyncJobStatus(selectedConnection.id, jobId);
                const job = idx(statusRes, _ => _.data) || statusRes;

                if (job && job.result) {
                    const resultsObj = job.result.result || job.result;
                    setSyncLogs(prev => {
                        const next = { ...prev };
                        Object.keys(resultsObj).forEach(id => {
                            const item = resultsObj[id];
                            if (item && typeof item === 'object' && item.status) {
                                next[id] = {
                                    status: item.status === 'success' ? 'success' : 'error',
                                    msg: item.msg
                                };
                            }
                        });
                        return next;
                    });
                }

                if (job && job.progress) {
                    setSyncProgress(job.progress);
                }

                if (job && job.status === 'completed') {
                    return job.result;
                } else if (job && job.status === 'failed') {
                    throw new Error(job.error || "Sync job failed in background");
                }
            } catch (err) {
                console.error("Error polling contact sync job:", err);
            }

            await new Promise(resolve => {
                const tId = setTimeout(resolve, intervalMs);
                if (!isMountedRef.current) clearTimeout(tId);
            });
        }
        throw new Error('Polling timeout exceeded');
    }, [selectedConnection]);

    // #11 — executeSyncContacts wrapped in useCallback
    const executeSyncContacts = useCallback(async () => {
        if (syncing) return;
        if (!selectedConnection || !accountId) return;
        if (syncDirection === 'pull') {
            await handleFetchPullPreview();
            return;
        }

        const currentRows = getActiveContactsForTab();
        const currentIds = currentRows.map(r => r.id);
        const idsToSync = selectedIds.length === 0 ? currentIds : selectedIds.filter(id => currentIds.includes(id));
 
        if (idsToSync.length === 0) {
            dispatch(errorSnackBar("No contacts available to push"));
            return;
        }

        syncHappenedRef.current = true;
        setSyncHappened(true);
        setSyncing(true);
        setSyncProgress({ total: idsToSync.length, current: 0 });

        const logs = {};
        idsToSync.forEach(id => {
            logs[id] = { status: 'pending', msg: 'Waiting in queue...' };
        });
        setSyncLogs(logs);

        const finalEntityType = entityType === 'all' ? activeModalTab : entityType;
        const userName = [idx(user, _ => _.first_name), idx(user, _ => _.last_name)].filter(Boolean).join(' ') || undefined;

        try {
            const res = await syncPlatformContacts(
                selectedConnection.id,
                finalEntityType,
                idsToSync,
                'push',
                undefined,
                { loader: loaders.NONE, successMessage: null }
            );
            const jobId = idx(res, _ => _.jobId) || idx(res, _ => _.data.jobId);

            if (jobId) {
                setSyncLogs(prev => {
                    const next = { ...prev };
                    idsToSync.forEach(id => {
                        next[id] = { status: 'syncing', msg: 'Syncing in background...' };
                    });
                    return next;
                });

                const pollRes = await pollSyncJob(jobId);
                
                if (!isMountedRef.current) return;

                if (pollRes) {
                    const resultsObj = pollRes.result || pollRes;
                    setSyncLogs(prev => {
                        const next = { ...prev };
                        Object.keys(resultsObj).forEach(id => {
                            const item = resultsObj[id];
                            if (item && typeof item === 'object' && item.status) {
                                next[id] = {
                                    status: item.status === 'success' ? 'success' : 'error',
                                    msg: item.msg
                                };
                            }
                        });
                        return next;
                    });
 
                    let successCount = 0;
                    let failCount = 0;
                    Object.values(resultsObj).forEach(item => {
                        if (item && typeof item === 'object' && item.status) {
                            if (item.status === 'success') successCount++;
                            else failCount++;
                        }
                    });

                    if (successCount > 0) {
                        dispatch(successSnackBar(`Successfully synced ${successCount} contact(s)`));
                    }
                    if (failCount > 0) {
                        dispatch(errorSnackBar(`Failed to sync ${failCount} contact(s). Check error details.`));
                    }
                }
            } else {
                dispatch(errorSnackBar(res?.error || 'Failed to queue export'));
            }
        } catch (err) {
            const errMsg = idx(err, _ => _.response.data.error) || err.message || 'Network/Server Error';
            dispatch(errorSnackBar(errMsg));
            setSyncLogs(prev => {
                const next = { ...prev };
                idsToSync.forEach(id => {
                    if (next[id]?.status === 'pending' || next[id]?.status === 'syncing') {
                        next[id] = { status: 'error', msg: errMsg };
                    }
                });
                return next;
            });
        } finally {
            if (isMountedRef.current) {
                setSyncing(false);
            }
        }
    }, [selectedConnection, accountId, syncDirection, handleFetchPullPreview, getActiveContactsForTab, selectedIds, entityType, activeModalTab, user, userId, dispatch, pollSyncJob]);

    const executePullSyncWithResolutions = useCallback(async () => {
        if (!selectedConnection || !accountId) return;
        try {
            syncHappenedRef.current = true;
            setSyncHappened(true);
            setSyncing(true);
            const finalEntityType = entityType === 'all' ? activeModalTab : entityType;
            const userName = [idx(user, _ => _.first_name), idx(user, _ => _.last_name)].filter(Boolean).join(' ') || undefined;
            const resolutionsPayload = Object.keys(resolutions).map(extId => ({
                externalId: extId,
                fields: resolutions[extId]
            }));
            const res = await syncPlatformContacts(
                selectedConnection.id,
                finalEntityType,
                selectedIds,
                'pull',
                resolutionsPayload,
                { loader: loaders.NONE, successMessage: null }
            );
            const jobId = idx(res, _ => _.jobId) || idx(res, _ => _.data.jobId);

            let result = null;
            if (jobId) {
                result = await pollSyncJob(jobId);
            }

            if (result && typeof result === 'object' && result.total !== undefined) {
                const { created, updated, failed } = result;
                if (failed > 0) {
                    if (created === 0 && updated === 0) {
                        dispatch(errorSnackBar(`Pull Synchronization Failed: All ${failed} contacts failed. Please check logs.`));
                    } else {
                        dispatch(errorSnackBar(`Pull Synchronization completed with errors: ${created} created, ${updated} updated, ${failed} failed.`));
                    }
                } else {
                    dispatch(successSnackBar(`Pull Synchronization completed: ${created} created, ${updated} updated.`));
                }
            } else if (!jobId) {
                dispatch(errorSnackBar('Failed to queue pull synchronization. Please try again.'));
                return;
            }
            setShowConflictModal(false);
        } catch (err) {
            dispatch(errorSnackBar(err.message || "Failed to pull contacts"));
        } finally {
            setSyncing(false);
            setSelectedIds([]);
        }
    }, [selectedConnection, accountId, entityType, activeModalTab, user, userId, resolutions, selectedIds, dispatch, pollSyncJob]);

    const handleClose = useCallback(() => {
        if ((syncHappened || syncHappenedRef.current) && onSyncSuccess) {
            onSyncSuccess();
        }
        onHide();
    }, [syncHappened, onSyncSuccess, onHide]);

    const getModalTitle = useCallback(() => {
        const platformName = selectedConnection ? (selectedConnection.platform === 'xero' ? 'Xero' : selectedConnection.platform === 'quickbooks' ? 'QuickBooks' : 'MYOB') : 'Platform';
        const ENTITY_LABELS = { customer: 'Customer ', supplier: 'Supplier ', contractor: 'Contractor ' };
        return `Sync ${ENTITY_LABELS[entityType] || ''}Contacts (${platformName})`;
    }, [selectedConnection, entityType]);

    return {
        // State
        connections, selectedConnection, setSelectedConnection,
        syncDirection, setSyncDirection,
        isPushAllowed, isPullAllowed,
        syncing, selectedIds, setSelectedIds,
        conflicts, showConflictModal, setShowConflictModal,
        fetchingPreview, resolutions, setResolutions,
        showAllSyncContacts, setShowAllSyncContacts,
        syncLogs, syncProgress,
        loadingMetadata, activeModalTab, setActiveModalTab,
        modalCustomers, modalSuppliers, modalContractors,
        // Actions
        getActiveContactsForTab, isAllSelectedForCurrentTab,
        toggleSelectAllForCurrentTab, toggleRowSelection,
        executeSyncContacts, executePullSyncWithResolutions,
        handleClose, getModalTitle
    };
}
